import { useEffect, useRef, useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Calendar, CornerDownRight, MessageSquare, MoreVertical } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TaskCard as TaskCardType, Project, TaskStatus, User } from '@witty-board/shared'
import { useUpdateTaskMutation } from '../../api/tasks.js'
import { TASK_STATUSES } from '../../lib/board.js'
import { Avatar } from '../../components/Avatar.js'
import { dueUrgency, formatDueDate } from '../../lib/due-date.js'

interface TaskCardProps {
  task: TaskCardType
  project: Project | undefined
  assignee: User | undefined
  onOpen: () => void
  /** "Mis tareas" (CLAUDE.md §6): la procedencia es la información crítica, así que la clave se ve en la fila de metadatos. */
  showProjectKey?: boolean
  /** El clon que sigue al cursor dentro de `DragOverlay` (TaskBoard.tsx). Comparte
   * `task.id` con la tarjeta que se queda en la columna, así que ambas instancias
   * de `useSortable` marcan `isDragging`: sin esta bandera, el clon también se
   * convertiría en el hueco vacío en vez de mostrar la tarjeta completa. */
  isOverlay?: boolean
}

const DUE_CLASSES = {
  overdue: 'text-danger',
  soon: 'text-warning',
  normal: 'text-text-muted',
} as const

/**
 * Alternativa al arrastre por menú contextual (CLAUDE.md §6: el drag & drop
 * necesita alternativa por teclado —ya la da `KeyboardSensor` de dnd-kit
 * sobre la propia tarjeta— y por menú contextual, que no existía en ninguna
 * fase anterior). Oculto hasta que se interactúa con la tarjeta en
 * escritorio (`group-hover`/`group-focus-within`), para no romper la
 * anatomía exacta de §6 en reposo; siempre visible en móvil, donde no hay
 * hover. Mueve al final de la columna destino: sin `position`, el backend ya
 * calcula "al final" (mismo camino que crear una tarea sin posición).
 */
function CardMoveMenu({ task }: { task: TaskCardType }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const update = useUpdateTaskMutation(task.id)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  function moveTo(status: TaskStatus): void {
    update.mutate({ status })
    setOpen(false)
  }

  return (
    <div ref={menuRef} className="relative shrink-0">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          setOpen((value) => !value)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
        }}
        aria-label={t('board.card.moveMenu')}
        aria-expanded={open}
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-card text-text-muted outline-none transition-opacity hover:bg-surface-hi hover:text-text focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent max-md:h-11 max-md:w-11 max-md:opacity-100 ${open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'}`}
      >
        <MoreVertical className="h-4 w-4" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          onClick={(event) => event.stopPropagation()}
          // Los ítems son descendientes de la tarjeta arrastrable: sin cortar
          // la propagación, Espacio/Intro los coge antes dnd-kit para
          // activar el arrastre que el propio botón (mismo problema que el
          // título, ver docs/DECISIONS.md fase 4).
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
          }}
          className="absolute right-0 top-full z-10 mt-1 w-44 rounded-panel bg-surface p-1 shadow"
        >
          {TASK_STATUSES.filter((status) => status !== task.status).map((status) => (
            <button
              key={status}
              type="button"
              role="menuitem"
              onClick={() => moveTo(status)}
              className="flex h-9 max-md:h-11 w-full items-center rounded-card px-3 text-left text-meta text-text hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              {t('board.card.moveTo', { column: t(`board.columns.${status}`) })}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Anatomía exacta de CLAUDE.md §6: franja de color a la izquierda, título +
 * pastilla de talla arriba (ancho fijo para que las tallas queden en
 * columna), fila de metadatos abajo. Sin prioridad ni etiquetas: eso se ve al
 * abrir la tarea.
 */
export function TaskCard({
  task,
  project,
  assignee,
  onOpen,
  showProjectKey = false,
  isOverlay = false,
}: TaskCardProps) {
  const { i18n } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
  })

  /** Hueco vacío en el origen mientras se arrastra (ver docs/DECISIONS.md):
   * el clon de `DragOverlay` sigue
   * al cursor con la tarjeta completa, y aquí se deja un espacio reservado
   * del mismo tamaño en vez de una tarjeta atenuada, para que el "cue" de
   * dónde va a caer sea inequívoco. */
  const showPlaceholder = isDragging && !isOverlay

  const style = { transform: CSS.Translate.toString(transform), transition }
  const urgency = task.dueDate ? dueUrgency(task.dueDate) : null
  const hasMeta =
    assignee !== undefined ||
    task.dueDate !== null ||
    task.commentCount > 0 ||
    task.subtaskCount > 0 ||
    (showProjectKey && project !== undefined)

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, borderLeftColor: showPlaceholder ? 'transparent' : project?.color }}
      {...attributes}
      {...listeners}
      tabIndex={0}
      onClick={onOpen}
      className={`group flex cursor-pointer touch-none flex-col gap-2 rounded-card border border-l-[3px] p-3 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
        showPlaceholder
          ? 'border-dashed border-border bg-transparent'
          : 'border-border bg-surface hover:bg-surface-hi'
      } ${isOverlay ? 'shadow' : ''}`}
    >
      <div className={showPlaceholder ? 'invisible contents' : 'contents'}>
        <div className="flex items-start justify-between gap-2">
          {/* Control propio, independiente de la tarjeta como asa de arrastre:
            Espacio/Intro sobre la tarjeta la cogen para moverla (dnd-kit), así
            que abrir el detalle por teclado necesita su propio elemento
            enfocable, con la propagación cortada para no disparar las dos
            cosas a la vez (ver docs/DECISIONS.md, fase 4). */}
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onOpen()
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
            }}
            className="min-w-0 flex-1 text-left text-card-title font-medium leading-snug text-text outline-none focus-visible:underline"
          >
            {task.title}
          </button>
          <CardMoveMenu task={task} />
          <span className="flex h-5 w-6 shrink-0 items-center justify-center rounded-card bg-surface-hi font-mono text-[11px] text-text-muted">
            {task.size ?? '–'}
          </span>
        </div>

        {hasMeta && (
          <div className="flex items-center gap-3 text-meta text-text-muted">
            {/* La clave va en `--text-muted`, no en `project.color`: el color de
              proyecto no cumple AA como color de texto sobre `--surface`
              (ver docs/DECISIONS.md, fase 6) — la identificación por color ya
              la dan el cuadrado de la barra lateral y esta misma franja
              izquierda, ambos decorativos y redundantes con el nombre. */}
            {showProjectKey && project && <span className="font-medium">{project.key}</span>}

            {assignee && (
              <span className="flex min-w-0 items-center gap-1" title={assignee.name}>
                <Avatar name={assignee.name} avatarUrl={assignee.avatarUrl} />
                <span className="max-w-[7rem] truncate">{assignee.name}</span>
              </span>
            )}

            {task.dueDate && urgency && (
              <span className={`flex items-center gap-1 ${DUE_CLASSES[urgency]}`}>
                <Calendar className="h-3 w-3" aria-hidden="true" />
                {formatDueDate(task.dueDate, i18n.language)}
              </span>
            )}

            {task.commentCount > 0 && (
              <span className="flex items-center gap-1">
                <MessageSquare className="h-3 w-3" aria-hidden="true" />
                {task.commentCount}
              </span>
            )}

            {task.subtaskCount > 0 && (
              <span className="flex items-center gap-1">
                <CornerDownRight className="h-3 w-3" aria-hidden="true" />
                {task.subtaskDoneCount}/{task.subtaskCount}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
